import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useSnackbar } from '../../../global/context/SnackbarContext'
import { ScrollToIds } from '../../../hooks/useScrollToElementIds'
import { FileIcon } from '../../../icons/FileIcon'
import { NameDto } from '../../../model/NameDto'
import {
  IntroducingBrokerRebateApprovalDto,
  RebateApprovalStatus,
  rebateApprovalEditableStatuses,
} from '../../../model/RebateApprovalsDto'
import { Text } from '../../../ui/Typography/Typography'
import { Modal } from '../../../ui/v2/Modal'
import { PageSection } from '../../../ui/v2/PageSection/PageSection'
import { Pagination } from '../../../ui/v2/Pagination/Pagination'
import { FilterOptions, TableFilter } from '../../../ui/v2/TableFilter/TableFilter'
import { TableSummary } from '../../../ui/v2/TableSummary/TableSummary'
import { useAccountReadContext } from '../../../utils/AccountContextContext'
import { ResponseError, useApiClient } from '../../../utils/ApiClient'
import { ClientApiClient } from '../../../utils/clientApi'
import { HeaderActions } from '../Common/HeaderActions'
import { useIBRebatesPageOutletContext } from '../RebatesPage'
import { ApprovalsPageTable } from './ApprovalsPageTable'
import { ButtonActions } from './ButtonActions'
import { RecalculateModal } from './RecalculateModal'
import { useIBApprovalsFilterOptions } from './hooks/useIBApprovalsFilterOptions'
import { useIBRebateApprovals } from './hooks/useIBRebateApprovals'
import { useIBRebateApprovalsSummary } from './hooks/useIBRebateApprovalsSummary'

import styles from './ApprovalsPage.module.scss'

export const IBRebateApprovalsPage: React.FC = () => {
  const { t } = useTranslation()
  const { account } = useAccountReadContext()

  const { addSnackbar } = useSnackbar()
  const [isFilterVisible, setIsFilterVisible] = useState(false)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [recalculateModal, setRecalculateModal] = useState<IntroducingBrokerRebateApprovalDto>()
  const [confirmationModal, setConfirmationModal] = useState<{
    type?: 'reject' | 'approve'
    group?: IntroducingBrokerRebateApprovalDto
  }>({})
  const { refreshIBNotifications } = useIBRebatesPageOutletContext()
  const { groups, tradingAccounts, accountTypes } = useIBApprovalsFilterOptions()

  const {
    query: tableQuery,
    sortOptions: tableSortOptions,
    search: tableSearch,
    setSearch: setTableSearch,
    forceRefresh: forceRefreshTable,
  } = useIBRebateApprovals({
    onCompleted: () => refreshIBNotifications(),
  })

  const clientApi = useApiClient(ClientApiClient)

  const {
    summaryItems,
    forceRefresh: forceRefreshApprovalsSummary,
    isLoading: isSummaryLoading,
  } = useIBRebateApprovalsSummary(tableSearch)

  const sortByStatusPriority = () => {
    tableQuery.setPageQuery?.({
      sort: 'statusPriority',
      sortOrder: 'ASC',
    })
  }

  const RebateApprovalStatusOptions: NameDto<RebateApprovalStatus>[] = [
    {
      id: RebateApprovalStatus.New,
      name: t('New'),
    },
    {
      id: RebateApprovalStatus.Pending,
      name: t('Pending'),
    },
    {
      id: RebateApprovalStatus.Approved,
      name: t('Approved'),
    },
    {
      id: RebateApprovalStatus.Rejected,
      name: t('Rejected'),
    },
    {
      id: RebateApprovalStatus.Failed,
      name: t('Failed'),
    },
    {
      id: RebateApprovalStatus.Reversed,
      name: t('Reversed'),
    },
  ]

  const handleApprove = async (ids: string[] | string) => {
    try {
      const ibId = account?.clientIntroducingBroker?.id ?? ''
      const isSingleId = typeof ids === 'string' || (Array.isArray(ids) && ids.length === 1)

      if (isSingleId) {
        const id = Array.isArray(ids) ? ids[0] : ids
        const response = await clientApi.approveSingleRebate(ibId, id)

        if (
          response.status.id === RebateApprovalStatus.Failed &&
          response.maxAllowedPercentage >= 0
        ) {
          addSnackbar.info({
            message: t('IB.RebatesPage.ApprovalsPage.walletInsufficientBalanceRecalculate', {
              percentage: response.maxAllowedPercentage,
            }),
          })
        } else {
          addSnackbar.success({
            message: t('IB.RebatesPage.ApprovalsPage.You have successfully approved_one', {
              count: 1,
            }),
          })
        }
      } else {
        const { skipped, processed } = await clientApi.approveBulkRebates(ibId, ids)
        if (processed.length) {
          addSnackbar.success({
            message: t('IB.RebatesPage.ApprovalsPage.You have successfully approved_other', {
              count: processed.length,
            }),
          })
        }
        if (skipped.length) {
          addSnackbar.warning({
            message: t(
              skipped.length === 1
                ? 'IB.RebatesPage.ApprovalsPage.Rebate Skipped_one'
                : 'IB.RebatesPage.ApprovalsPage.Rebate Skipped_other',
              {
                count: skipped.length,
              }
            ),
          })
        }
      }

      if (!tableQuery.pageQuery?.sort) {
        sortByStatusPriority()
      }

      refreshData()
    } catch (error: unknown) {
      if (error instanceof ResponseError) {
        if (error.response.response?.data.code === 'rebate_payout_status_can_not_be_changed') {
          addSnackbar.error({
            message: t('IB.RebatesPage.ApprovalsPage.Cannot update finalized rebates'),
          })
        } else if (error.response.response?.data.code === 'insufficient_wallet_balance') {
          addSnackbar.error({
            message: t('errors.Wallet has insufficient balance'),
          })
          refreshData()
        }
      }
    }
  }

  const handleReject = async (ids: string[] | string) => {
    try {
      const ibId = account?.clientIntroducingBroker?.id ?? ''

      if (typeof ids === 'string') {
        await clientApi.rejectRebate(ibId, ids)
        addSnackbar.success({
          message: t('IB.RebatesPage.ApprovalsPage.You have successfully rejected_one', {
            count: 1,
          }),
        })
      } else {
        const { skipped, processed } = await clientApi.rejectBulkRebates(ibId, ids)
        if (processed.length) {
          addSnackbar.success({
            message: t('IB.RebatesPage.ApprovalsPage.You have successfully rejected_other', {
              count: processed.length,
            }),
          })
        }
        if (skipped.length) {
          addSnackbar.warning({
            message: t(
              skipped.length === 1
                ? 'IB.RebatesPage.ApprovalsPage.Rebate Skipped_one'
                : 'IB.RebatesPage.ApprovalsPage.Rebate Skipped_other',
              {
                count: skipped.length,
              }
            ),
          })
        }
      }

      if (!tableQuery.pageQuery?.sort) {
        sortByStatusPriority()
      }

      refreshData()
    } catch (error: unknown) {
      if (
        error instanceof ResponseError &&
        error.response.response?.data.code === 'rebate_payout_status_can_not_be_changed'
      ) {
        addSnackbar.error({
          message: t('IB.RebatesPage.ApprovalsPage.Cannot update finalized rebates'),
        })
      }
    }
  }

  const handleRecalculate = async (approvalId: string, percentage: number) => {
    try {
      if (!account?.clientIntroducingBroker?.id) {
        return
      }
      await clientApi.recalculateRebate(account?.clientIntroducingBroker.id, approvalId, percentage)
      addSnackbar.success({
        message: t('IB.RebatesPage.ApprovalsPage.Successfully recalculated rebate'),
      })
      setRecalculateModal(undefined)
      refreshData()
    } catch (error: unknown) {
      if (
        error instanceof ResponseError &&
        error.response.response?.data.code === 'rebate_payout_status_can_not_be_changed'
      ) {
        addSnackbar.error({
          message: t('IB.RebatesPage.ApprovalsPage.Cannot update finalized rebates'),
        })
      }
    }
  }

  const handlePageChange = (pageIndex: number, pageSize: number) => {
    tableQuery.setPageQuery?.({
      ...tableQuery.pageQuery,
      pageIndex,
      pageSize,
    })
  }

  const isAnyFilterActive = useMemo(
    () => Object.values(tableSearch || {}).some((value) => !!value),
    [tableSearch]
  )

  const refreshData = () => {
    setSelectedIds([])
    forceRefreshApprovalsSummary()
    forceRefreshTable()
  }

  const handleCheckBoxClick = () => {
    if (selectedIds.length > 0) {
      setSelectedIds([])

      return
    }

    setSelectedIds(
      tableQuery.data
        .filter((item) => rebateApprovalEditableStatuses.includes(item.status.id))
        .map((item) => item.id)
    )
  }

  const handleConfirm = async () => {
    if (confirmationModal.type === 'approve') {
      await handleApprove(confirmationModal.group?.id ?? selectedIds)
    } else if (confirmationModal.type === 'reject') {
      await handleReject(confirmationModal.group?.id ?? selectedIds)
    }
    setConfirmationModal({})
  }

  const getRebatePercentage = (rebate: IntroducingBrokerRebateApprovalDto) =>
    Math.round((rebate.rebateAmount / rebate.accumulatedCommission) * 100)

  return (
    <div className={styles.approvalsPage}>
      <TableSummary isLoading={isSummaryLoading} summaryItems={summaryItems} />

      <div className={styles.container}>
        <PageSection
          icon={FileIcon}
          title={t('IB.RebatesPage.Trading accounts')}
          id={ScrollToIds.IBRebateApprovalsHeader}
        >
          <HeaderActions onFiltersClick={() => setIsFilterVisible((prevState) => !prevState)} />
        </PageSection>
        {!!confirmationModal.type && (
          <Modal.Confirm
            size='ib-sm'
            title={t('Please, confirm your action')}
            onClose={() => setConfirmationModal({})}
            onConfirm={handleConfirm}
            isValid
          >
            <Text>
              {t(
                confirmationModal.type === 'approve'
                  ? selectedIds.length === 1
                    ? 'IB.RebatesPage.ApprovalsPage.You are about to Approve_one'
                    : 'IB.RebatesPage.ApprovalsPage.You are about to Approve_other'
                  : selectedIds.length === 1
                  ? 'IB.RebatesPage.ApprovalsPage.You are about to Reject_one'
                  : 'IB.RebatesPage.ApprovalsPage.You are about to Reject_other',
                { count: selectedIds.length || 1 }
              )}
            </Text>
          </Modal.Confirm>
        )}
        {!!recalculateModal && (
          <RecalculateModal
            currentPercentage={getRebatePercentage(recalculateModal)}
            onCancel={() => setRecalculateModal(undefined)}
            onConfirm={(percentage) => handleRecalculate(recalculateModal.id, percentage)}
          />
        )}
        <TableFilter
          isVisible={isFilterVisible}
          searchFilters={tableSearch}
          filterOptions={[
            FilterOptions.TradingAccountId,
            FilterOptions.TradingAccountType,
            FilterOptions.IBRebateGroupId,
            FilterOptions.AccumulatedVolumeFxm,
            FilterOptions.AccumulatedVolumeOther,
            FilterOptions.AccumulatedCommission,
            FilterOptions.RebateAmount,
            FilterOptions.Status,
          ]}
          statusOptions={RebateApprovalStatusOptions}
          customLabels={{
            [FilterOptions.IBRebateGroupId]: t('IB.RebatesPage.ClientsPage.Group'),
          }}
          groupSearchProps={{
            options: groups || [],
          }}
          tradingAccountTypeOptions={accountTypes || []}
          tradingAccountSearchProps={{
            options: tradingAccounts || [],
          }}
          setSearchFilters={(searchFilters) => {
            if (!searchFilters) {
              return
            }
            setTableSearch(searchFilters)
            tableQuery.setPageQuery?.({
              ...tableQuery.pageQuery,
              pageIndex: 1,
            })
          }}
        />
        <ButtonActions
          isCheckBoxActive={selectedIds.length > 0}
          onCheckBoxClick={handleCheckBoxClick}
          hasData={selectedIds.length > 0}
          onApprove={() => setConfirmationModal({ type: 'approve' })}
          onReject={() => setConfirmationModal({ type: 'reject' })}
        />
        <div className={styles.tableContainer}>
          <ApprovalsPageTable
            tableQuery={tableQuery}
            sortOptions={tableSortOptions}
            sticky='left'
            isAnyFilterActive={isAnyFilterActive}
            onSelect={setSelectedIds}
            selectedIds={selectedIds}
            onRecalculate={(group) => setRecalculateModal(group)}
            onApprove={(group) => setConfirmationModal({ type: 'approve', group })}
            onReject={(group) => setConfirmationModal({ type: 'reject', group })}
          />

          {tableQuery.meta && tableQuery.hasInitialResults && (
            <Pagination
              scrollToHeaderId={ScrollToIds.IBRebateApprovalsHeader}
              maxPageSize={100}
              pageData={tableQuery.meta}
              isLoading={tableQuery.isLoading}
              onPageChanged={handlePageChange}
            />
          )}
        </div>
      </div>
    </div>
  )
}
